import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../config/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to check if Supabase is ready
const checkSupabase = () => {
  if (!isSupabaseConfigured || !supabase) {
    console.warn('Supabase not configured. Operations will return mock data.');
    return false;
  }
  return true;
};

const _createAdminNotification = async (category, title, description, referenceId, link) => {
  if (!supabase) return;
  try {
    await supabase.from('admin_notifications').insert({
      category,
      title,
      description: description || null,
      reference_id: referenceId || null,
      link: link || null,
    });
  } catch (err) {
    console.error('Admin notification error:', err);
  }
};

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ========================================
  // FILE UPLOAD FUNCTIONS (defined first to avoid initialization errors)
  // ========================================

  // Upload image to Supabase storage
  const uploadImage = useCallback(async (file, bucket = 'spotlight-images') => {
    if (!checkSupabase()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log('Uploading file:', fileName, 'to bucket:', bucket);

      const { data, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload success:', data);

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      console.log('Public URL:', urlData.publicUrl);

      return { success: true, url: urlData.publicUrl };
    } catch (err) {
      console.error('Upload failed:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // ========================================
  // AUTHENTICATION FUNCTIONS (Using admin table)
  // ========================================

  // Admin Sign In - Check against admin table
  const signIn = useCallback(async (email, password) => {
    if (!checkSupabase()) {
      return { success: false, error: 'Supabase not configured' };
    }
    setLoading(true);
    setError(null);
    try {
      // Call Supabase RPC function to verify admin credentials
      const { data, error: rpcError } = await supabase.rpc('verify_admin_login', {
        p_email: email,
        p_password: password
      });

      if (rpcError) {
        console.error('Login RPC error:', rpcError);
        return { success: false, error: 'Invalid email or password' };
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Invalid email or password' };
      }

      const admin = data[0];

      // Store session info (24 hours)
      const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000);
      localStorage.setItem('adminToken', `admin-token-${admin.id}`);
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      localStorage.setItem('adminUser', JSON.stringify({
        email: admin.email,
        name: admin.name,
        id: admin.id,
        profile_image: admin.profile_image || null
      }));

      return { success: true, data: admin };
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin Sign Out
  const signOut = useCallback(async () => {
    if (!checkSupabase()) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('adminUser');
      return { success: true };
    }
    setLoading(true);
    setError(null);
    try {
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) throw signOutError;

      // Clear local storage
      localStorage.removeItem('adminToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('adminUser');

      return { success: true };
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update Password (for logged-in admin or with current password verification)
  const updatePassword = useCallback(async (email, currentPassword, newPassword) => {
    if (!checkSupabase()) {
      return { success: false, error: 'Supabase not configured' };
    }
    setLoading(true);
    setError(null);
    try {
      // Call RPC function to update admin password
      const { data, error: rpcError } = await supabase.rpc('update_admin_password', {
        p_email: email,
        p_current_password: currentPassword,
        p_new_password: newPassword
      });

      if (rpcError) {
        console.error('Password update error:', rpcError);
        return { success: false, error: 'Failed to update password. Please check your current password.' };
      }

      if (!data) {
        return { success: false, error: 'Invalid current password' };
      }

      return { success: true, message: 'Password updated successfully' };
    } catch (err) {
      console.error('Password update error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // ADMIN PROFILE IMAGE FUNCTIONS
  // ========================================

  // Upload and update admin profile image (uses RPC to bypass RLS)
  const updateAdminProfileImage = useCallback(async (file) => {
    if (!checkSupabase()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      // Upload image using existing uploadImage function
      const uploadResult = await uploadImage(file);
      if (!uploadResult.success) {
        return { success: false, error: uploadResult.error };
      }

      // Update admin record via RPC (bypasses RLS)
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const { error: rpcError } = await supabase.rpc('update_admin_profile_image', {
        p_email: adminUser.email,
        p_image_url: uploadResult.url
      });

      if (rpcError) {
        console.error('Failed to update admin profile_image:', rpcError);
        return { success: false, error: rpcError.message };
      }

      // Update localStorage
      adminUser.profile_image = uploadResult.url;
      localStorage.setItem('adminUser', JSON.stringify(adminUser));

      return { success: true, url: uploadResult.url };
    } catch (err) {
      console.error('Update admin profile image error:', err);
      return { success: false, error: err.message };
    }
  }, [uploadImage]);

  // Fetch admin profile image from database via RPC (bypasses RLS)
  const fetchAdminProfileImage = useCallback(async () => {
    if (!checkSupabase()) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const { data, error: rpcError } = await supabase.rpc('get_admin_profile_image', {
        p_email: adminUser.email
      });

      if (rpcError) {
        console.error('Failed to fetch admin profile image:', rpcError);
        return { success: false, error: rpcError.message };
      }

      return { success: true, url: data || null };
    } catch (err) {
      console.error('Fetch admin profile image error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // ========================================
  // NOMINATION FUNCTIONS
  // ========================================

  // Submit a new nomination (user-side)
  const submitNomination = useCallback(async (nominationData) => {
    if (!checkSupabase()) {
      return { success: true, data: { id: 'mock-id', ...nominationData }, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      // Ensure secondary_sports is an array
      let secondarySportsArray = nominationData.secondarySports;
      if (typeof secondarySportsArray === 'string') {
        secondarySportsArray = secondarySportsArray ? [secondarySportsArray] : [];
      } else if (!Array.isArray(secondarySportsArray)) {
        secondarySportsArray = [];
      }

      const { data, error: submitError } = await supabase
        .from('nominations')
        .insert([{
          first_name: nominationData.firstName,
          last_name: nominationData.lastName,
          primary_sport: nominationData.primarySport,
          secondary_sports: secondarySportsArray,
          city: nominationData.city,
          state: nominationData.state,
          reason: nominationData.reason,
          athlete_photos: nominationData.athletePhotos || [],
          video_uid: nominationData.videoUid || null,
          submitter_email: nominationData.email,
          submitter_phone: nominationData.phone,
          nominator_name: nominationData.nominatorName || null,
          notify_submitter: nominationData.notifyMe || false,
          status: 'pending',
          submitted_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (submitError) throw submitError;

      // If user opted in for notifications, add to email list
      if (nominationData.notifyMe && nominationData.email) {
        await addEmailSubscriber(nominationData.email);
      }

      if (data?.id) {
        _createAdminNotification(
          'nomination',
          'New Spotlight Nomination',
          `${nominationData.firstName} ${nominationData.lastName} — ${nominationData.primarySport}`,
          data.id,
          '/dashboard/nominations'
        ).catch(() => {});
      }

      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get nominations with optional status filter (admin-side)
  const getNominations = useCallback(async (status = null) => {
    if (!checkSupabase()) {
      return { success: true, data: [], mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('nominations')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update nomination status (approve, reject, hold)
  const updateNominationStatus = useCallback(async (id, status) => {
    if (!checkSupabase()) {
      return { success: true, data: { id, status }, mock: true };
    }
    console.log('Updating nomination ID:', id, 'to status:', status);
    setLoading(true);
    setError(null);
    try {
      const { data, error: updateError } = await supabase
        .from('nominations')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Admin notification on approve/reject
      const athleteName = `${data?.first_name || ''} ${data?.last_name || ''}`.trim() || 'Athlete';
      if (status === 'approved') {
        _createAdminNotification('nomination', 'Spotlight Athlete Approved',
          `${athleteName} was approved for Spotlight`, id, '/dashboard/nominations').catch(() => {});
      } else if (status === 'rejected') {
        _createAdminNotification('nomination', 'Spotlight Athlete Rejected',
          `${athleteName} was rejected for Spotlight`, id, '/dashboard/nominations').catch(() => {});
      }

      return { success: true, data };
    } catch (err) {
      console.error('Update nomination status error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update nomination video reviewed status
  const updateNominationVideoReviewed = useCallback(async (id, videoReviewed) => {
    if (!checkSupabase()) {
      return { success: true, data: { id, video_reviewed: videoReviewed }, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: updateError } = await supabase
        .from('nominations')
        .update({
          video_reviewed: videoReviewed,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      return { success: true, data };
    } catch (err) {
      console.error('Update video reviewed error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteNomination = useCallback(async (id) => {
    if (!checkSupabase()) return { success: true, mock: true };
    try {
      // Also delete any spotlight linked to this nomination
      await supabase.from('spotlights').delete().eq('nomination_id', id);
      const { error } = await supabase.from('nominations').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ========================================
  // SPOTLIGHT FUNCTIONS
  // ========================================

  // Create a spotlight from nomination
 const createSpotlight = useCallback(async (nominationId, spotlightData) => {
  if (!checkSupabase()) {
    return { success: true, data: { id: 'mock-spotlight-id', ...spotlightData }, mock: true };
  }
  setLoading(true);
  setError(null);
  try {
    // First, get the nomination data
    const { data: nomination, error: nomError } = await supabase
      .from('nominations')
      .select('*')
      .eq('id', nominationId)
      .single();

    if (nomError) throw nomError;

    // Upload hero image if it's a file
    let heroImageUrl = spotlightData.heroImage;
    if (spotlightData.heroImage && spotlightData.heroImage instanceof File) {
      const uploadResult = await uploadImage(spotlightData.heroImage);
      if (uploadResult.success) {
        heroImageUrl = uploadResult.url;
      }
    }

    // moment_media should only contain image URLs (not hero image, not video)
    // The frontend already filters out the hero image, so we just need to handle the URLs
    let momentMediaUrls = [];
    if (spotlightData.momentMedia && Array.isArray(spotlightData.momentMedia)) {
      // momentMedia is already an array of URLs (strings) from the frontend
      momentMediaUrls = spotlightData.momentMedia.map(url => ({
        url: url,
        type: 'image'
      }));
    }

    // Create the spotlight
    const { data: spotlight, error: spotlightError } = await supabase
      .from('spotlights')
      .insert([{
        nomination_id: nominationId,
        athlete_name: `${nomination.first_name} ${nomination.last_name}`,
        primary_sport: nomination.primary_sport,
        secondary_sports: nomination.secondary_sports,
        city: nomination.city,
        state: nomination.state,
        hero_image: heroImageUrl,
        video_uid: spotlightData.videoUid || null,
        moment_headline: spotlightData.momentHeadline || 'The Moment',
        regional_label: spotlightData.regionalLabel || '',
        moment_description: spotlightData.momentDescription || '',
        moment_media: momentMediaUrls,
        official_caption: spotlightData.officialCaption || '',
        time_label: spotlightData.timeLabel || '',
        status: 'published',
        published_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (spotlightError) throw spotlightError;

    // CRITICAL: Update nomination status to spotlighted
    const { error: updateNomError } = await supabase
      .from('nominations')
      .update({ 
        status: 'spotlighted',
        spotlight_id: spotlight.id,
        updated_at: new Date().toISOString()
      })
      .eq('id', nominationId);

    if (updateNomError) {
      console.error('Failed to update nomination status:', updateNomError);
      // Don't throw - we still created the spotlight successfully
      // Just log the error
    } else {
      console.log('Successfully updated nomination status to spotlighted');
    }

    // Check if analytics record already exists before inserting
    const { data: existingAnalytics } = await supabase
      .from('analytics')
      .select('id')
      .eq('spotlight_id', spotlight.id)
      .single();

    // Only create analytics if it doesn't exist
    if (!existingAnalytics) {
      const { error: analyticsError } = await supabase
        .from('analytics')
        .insert([{
          spotlight_id: spotlight.id,
          views: 0,
          unique_views: 0,
          shares: 0,
          unique_shares: 0,
          link_copies: 0,
          unique_link_copies: 0,
          caption_copies: 0,
          unique_caption_copies: 0,
          downloads: 0,
          unique_downloads: 0
        }]);

      if (analyticsError) {
        console.warn('Analytics record already exists or could not be created:', analyticsError);
      }
    }

    return { 
      success: true, 
      data: spotlight,
      nominationUpdated: !updateNomError // Return whether nomination was updated
    };
  } catch (err) {
    console.error('Create spotlight error:', err);
    setError(err.message);
    return { success: false, error: err.message };
  } finally {
    setLoading(false);
  }
}, [uploadImage]);

  // Create a spotlight directly without a nomination
  const createSpotlightDirect = useCallback(async (spotlightData) => {
    if (!checkSupabase()) {
      return { success: true, data: { id: 'mock-spotlight-id', ...spotlightData }, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      // Upload hero image if it's a file
      let heroImageUrl = spotlightData.heroImage;
      if (spotlightData.heroImage && spotlightData.heroImage instanceof File) {
        const uploadResult = await uploadImage(spotlightData.heroImage);
        if (uploadResult.success) {
          heroImageUrl = uploadResult.url;
        }
      }

      // moment_media should only contain image URLs (not hero image, not video)
      // The frontend already filters out the hero image, so we just need to handle the URLs
      let momentMediaUrls = [];
      if (spotlightData.momentMedia && Array.isArray(spotlightData.momentMedia)) {
        // momentMedia is already an array of URLs (strings) from the frontend
        momentMediaUrls = spotlightData.momentMedia.map(url => ({
          url: url,
          type: 'image'
        }));
      }

      // Create the spotlight
      const { data: spotlight, error: spotlightError } = await supabase
        .from('spotlights')
        .insert([{
          nomination_id: null,
          athlete_name: spotlightData.athleteName,
          athlete_user_id: spotlightData.athleteUserId || null,
          primary_sport: spotlightData.primarySport || '',
          secondary_sports: spotlightData.secondarySports || [],
          city: spotlightData.city || '',
          state: spotlightData.state || '',
          hero_image: heroImageUrl,
          video_uid: spotlightData.videoUid || null,
          moment_headline: spotlightData.momentHeadline || 'The Moment',
          regional_label: spotlightData.regionalLabel || '',
          moment_description: spotlightData.momentDescription || '',
          moment_media: momentMediaUrls,
          official_caption: spotlightData.officialCaption || '',
          time_label: spotlightData.timeLabel || '',
          status: 'published',
          published_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (spotlightError) throw spotlightError;

      // Create analytics record for the spotlight
      await supabase
        .from('analytics')
        .insert([{
          spotlight_id: spotlight.id,
          views: 0,
          unique_views: 0,
          shares: 0,
          unique_shares: 0,
          link_copies: 0,
          unique_link_copies: 0,
          caption_copies: 0,
          unique_caption_copies: 0,
          downloads: 0,
          unique_downloads: 0
        }]);

      return { success: true, data: spotlight };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get all spotlights
  const getSpotlights = useCallback(async (status = null) => {
    if (!checkSupabase()) {
      return { success: true, data: [], mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('spotlights')
        .select(`
          *,
          analytics (*)
        `)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get single spotlight by ID
  const getSpotlightById = useCallback(async (id) => {
    if (!checkSupabase()) {
      return { success: true, data: null, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('spotlights')
        .select(`
          *,
          analytics (*),
          featured_athletes (order_position)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to check if URL is a YouTube URL
  const isYouTubeUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Update spotlight
  const updateSpotlight = useCallback(async (id, spotlightData) => {
    if (!checkSupabase()) {
      return { success: true, data: spotlightData, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      // Handle hero image upload if it's a file
      let updateData = { ...spotlightData };
      if (spotlightData.hero_image && spotlightData.hero_image instanceof File) {
        const uploadResult = await uploadImage(spotlightData.hero_image);
        if (uploadResult.success) {
          updateData.hero_image = uploadResult.url;
        }
      }

      // Handle moment media uploads if they're files
      if (spotlightData.moment_media && Array.isArray(spotlightData.moment_media)) {
        console.log('Processing moment_media:', spotlightData.moment_media);
        let momentMediaUrls = [];
        for (const media of spotlightData.moment_media) {
          console.log('Processing media item:', media, 'Has file:', media.file instanceof File, 'Has url:', !!media.url);

          if (media.file instanceof File) {
            // Upload new file (images only - videos are YouTube URLs now)
            console.log('Uploading new file:', media.file.name);
            const uploadResult = await uploadImage(media.file);
            console.log('Upload result:', uploadResult);
            if (uploadResult.success) {
              momentMediaUrls.push({
                url: uploadResult.url,
                type: media.type || 'image'
              });
              console.log('Added uploaded media URL:', uploadResult.url);
            } else {
              console.error('Failed to upload media:', uploadResult.error);
            }
          } else if (media.url) {
            // Check if it's a YouTube URL (video) or regular URL (image)
            if (isYouTubeUrl(media.url)) {
              // YouTube URL - store directly without uploading
              momentMediaUrls.push({
                url: media.url,
                type: 'video'
              });
              console.log('Keeping YouTube URL:', media.url);
            } else {
              // Regular URL - keep it (existing image)
              momentMediaUrls.push({
                url: media.url,
                type: media.type || 'image'
              });
              console.log('Keeping existing media URL:', media.url);
            }
          } else {
            console.warn('Skipping media without file or url:', media);
          }
        }
        console.log('Final momentMediaUrls to save:', momentMediaUrls);
        updateData.moment_media = momentMediaUrls;
      }

      console.log('Saving to database with updateData:', updateData);

      const { data, error: updateError } = await supabase
        .from('spotlights')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('Database update error:', updateError);
        throw updateError;
      }

      console.log('Database update success:', data);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [uploadImage]);

  // Delete spotlight
  const deleteSpotlight = useCallback(async (id) => {
    if (!checkSupabase()) {
      return { success: true, mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      // First remove from featured if exists
      await supabase
        .from('featured_athletes')
        .delete()
        .eq('spotlight_id', id);

      // Delete analytics
      await supabase
        .from('analytics')
        .delete()
        .eq('spotlight_id', id);

      // Delete spotlight
      const { error: deleteError } = await supabase
        .from('spotlights')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // FEATURED ATHLETES FUNCTIONS
  // ========================================

  // Get featured athletes (ordered)
  const getFeaturedAthletes = useCallback(async (visibleOnly = false) => {
    if (!checkSupabase()) {
      return { success: true, data: [], mock: true };
    }
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('featured_athletes')
        .select(`
          *,
          spotlight:spotlights (*)
        `)
        .order('order_position', { ascending: true });

      if (visibleOnly) {
        query = query.eq('is_visible', true);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Add spotlight to featured
  const addToFeatured = useCallback(async (spotlightId) => {
    setLoading(true);
    setError(null);
    try {
      // Get current max position
      const { data: existing } = await supabase
        .from('featured_athletes')
        .select('order_position')
        .order('order_position', { ascending: false })
        .limit(1);

      const nextPosition = existing && existing.length > 0 
        ? existing[0].order_position + 1 
        : 1;

      const { data, error: insertError } = await supabase
        .from('featured_athletes')
        .insert([{
          spotlight_id: spotlightId,
          order_position: nextPosition,
          is_visible: true,
          added_at: new Date().toISOString()
        }])
        .select(`
          *,
          spotlight:spotlights (*)
        `)
        .single();

      if (insertError) throw insertError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update featured athlete order
  const updateFeaturedOrder = useCallback(async (id, newPosition) => {
    setLoading(true);
    setError(null);
    try {
      // Get all featured athletes
      const { data: allFeatured } = await supabase
        .from('featured_athletes')
        .select('*')
        .order('order_position', { ascending: true });

      const currentItem = allFeatured.find(item => item.id === id);
      const currentPosition = currentItem.order_position;

      if (newPosition === currentPosition) {
        return { success: true };
      }

      // Reorder items
      const updates = [];
      allFeatured.forEach(item => {
        if (item.id === id) {
          updates.push({ id: item.id, order_position: newPosition });
        } else if (newPosition < currentPosition) {
          // Moving up: shift items between new and old position down
          if (item.order_position >= newPosition && item.order_position < currentPosition) {
            updates.push({ id: item.id, order_position: item.order_position + 1 });
          }
        } else {
          // Moving down: shift items between old and new position up
          if (item.order_position > currentPosition && item.order_position <= newPosition) {
            updates.push({ id: item.id, order_position: item.order_position - 1 });
          }
        }
      });

      // Apply updates
      for (const update of updates) {
        await supabase
          .from('featured_athletes')
          .update({ order_position: update.order_position })
          .eq('id', update.id);
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Move featured athlete up
  const moveFeaturedUp = useCallback(async (id) => {
    const { data: featured } = await supabase
      .from('featured_athletes')
      .select('*')
      .order('order_position', { ascending: true });

    const index = featured.findIndex(item => item.id === id);
    if (index > 0) {
      const currentPosition = featured[index].order_position;
      const aboveId = featured[index - 1].id;
      const abovePosition = featured[index - 1].order_position;

      await supabase.from('featured_athletes').update({ order_position: abovePosition }).eq('id', id);
      await supabase.from('featured_athletes').update({ order_position: currentPosition }).eq('id', aboveId);
    }
    return { success: true };
  }, []);

  // Move featured athlete down
  const moveFeaturedDown = useCallback(async (id) => {
    const { data: featured } = await supabase
      .from('featured_athletes')
      .select('*')
      .order('order_position', { ascending: true });

    const index = featured.findIndex(item => item.id === id);
    if (index < featured.length - 1) {
      const currentPosition = featured[index].order_position;
      const belowId = featured[index + 1].id;
      const belowPosition = featured[index + 1].order_position;

      await supabase.from('featured_athletes').update({ order_position: belowPosition }).eq('id', id);
      await supabase.from('featured_athletes').update({ order_position: currentPosition }).eq('id', belowId);
    }
    return { success: true };
  }, []);

  // Toggle featured visibility
  const toggleFeaturedVisibility = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Get current visibility
      const { data: current } = await supabase
        .from('featured_athletes')
        .select('is_visible')
        .eq('id', id)
        .single();

      const { data, error: updateError } = await supabase
        .from('featured_athletes')
        .update({ is_visible: !current.is_visible })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove from featured (and recompact order)
  const removeFromFeatured = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Get the item to be removed
      const { data: toRemove } = await supabase
        .from('featured_athletes')
        .select('order_position')
        .eq('id', id)
        .single();

      // Delete the item
      const { error: deleteError } = await supabase
        .from('featured_athletes')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Recompact order - move all items after the deleted one up
      const { data: remaining } = await supabase
        .from('featured_athletes')
        .select('*')
        .gt('order_position', toRemove.order_position);

      for (const item of remaining || []) {
        await supabase
          .from('featured_athletes')
          .update({ order_position: item.order_position - 1 })
          .eq('id', item.id);
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // ANALYTICS FUNCTIONS
  // ========================================

  // Track an analytics event
  const trackAnalytics = useCallback(async (spotlightId, action, userId = null) => {
    try {
      // Generate a session ID if no user ID (for unique tracking)
      const sessionId = userId || localStorage.getItem('session_id') || (() => {
        const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('session_id', id);
        return id;
      })();

      // Map action to column names
      const columnMap = {
        view: 'views',
        share: 'shares',
        copy_link: 'link_copies',
        copy_caption: 'caption_copies',
        download: 'downloads'
      };

      const uniqueColumnMap = {
        view: 'unique_views',
        share: 'unique_shares',
        copy_link: 'unique_link_copies',
        copy_caption: 'unique_caption_copies',
        download: 'unique_downloads'
      };

      const column = columnMap[action];
      const uniqueColumn = uniqueColumnMap[action];

      if (!column) return { success: false, error: 'Invalid action' };

      // Check if this is a unique action (first time for this session)
      const trackingKey = `${spotlightId}_${action}_${sessionId}`;
      const isUnique = !localStorage.getItem(trackingKey);

      // Get current analytics
      const { data: current } = await supabase
        .from('analytics')
        .select('*')
        .eq('spotlight_id', spotlightId)
        .single();

      if (!current) {
        // Create analytics record if doesn't exist
        const insertData = {
          spotlight_id: spotlightId,
          views: 0, unique_views: 0,
          shares: 0, unique_shares: 0,
          link_copies: 0, unique_link_copies: 0,
          caption_copies: 0, unique_caption_copies: 0,
          downloads: 0, unique_downloads: 0
        };
        insertData[column] = 1;
        if (isUnique) insertData[uniqueColumn] = 1;

        await supabase.from('analytics').insert([insertData]);
      } else {
        // Update existing analytics
        const updateData = {
          [column]: current[column] + 1
        };
        if (isUnique) {
          updateData[uniqueColumn] = current[uniqueColumn] + 1;
        }

        await supabase
          .from('analytics')
          .update(updateData)
          .eq('spotlight_id', spotlightId);
      }

      // Mark as tracked for uniqueness
      if (isUnique) {
        localStorage.setItem(trackingKey, 'true');
      }

      return { success: true };
    } catch (err) {
      console.error('Analytics tracking error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Get analytics for a single spotlight
  const getSpotlightAnalytics = useCallback(async (spotlightId) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('analytics')
        .select('*')
        .eq('spotlight_id', spotlightId)
        .single();

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Get all analytics with spotlight info
  const getAllAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('analytics')
        .select(`
          *,
          spotlight:spotlights (
            id,
            athlete_name,
            primary_sport,
            status
          )
        `);

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get aggregated analytics totals
  const getAnalyticsTotals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('analytics')
        .select('*');

      if (fetchError) throw fetchError;

      // Aggregate totals
      const totals = data.reduce((acc, item) => ({
        views: acc.views + (item.views || 0),
        unique_views: acc.unique_views + (item.unique_views || 0),
        shares: acc.shares + (item.shares || 0),
        unique_shares: acc.unique_shares + (item.unique_shares || 0),
        link_copies: acc.link_copies + (item.link_copies || 0),
        unique_link_copies: acc.unique_link_copies + (item.unique_link_copies || 0),
        caption_copies: acc.caption_copies + (item.caption_copies || 0),
        unique_caption_copies: acc.unique_caption_copies + (item.unique_caption_copies || 0),
        downloads: acc.downloads + (item.downloads || 0),
        unique_downloads: acc.unique_downloads + (item.unique_downloads || 0)
      }), {
        views: 0, unique_views: 0,
        shares: 0, unique_shares: 0,
        link_copies: 0, unique_link_copies: 0,
        caption_copies: 0, unique_caption_copies: 0,
        downloads: 0, unique_downloads: 0
      });

      return { success: true, data: totals };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // STANDOUT ANALYTICS FUNCTIONS
  // ========================================

  const trackStandoutAnalytics = useCallback(async (standoutId, action) => {
    try {
      const sessionId = localStorage.getItem('session_id') || (() => {
        const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('session_id', id);
        return id;
      })();

      const columnMap = {
        view: 'views',
        share: 'shares',
        copy_link: 'link_copies',
        download: 'downloads'
      };
      const uniqueColumnMap = {
        view: 'unique_views',
        share: 'unique_shares',
        copy_link: 'unique_link_copies',
        download: 'unique_downloads'
      };

      const column = columnMap[action];
      const uniqueColumn = uniqueColumnMap[action];
      if (!column) return { success: false, error: 'Invalid action' };

      const trackingKey = `so_${standoutId}_${action}_${sessionId}`;
      const isUnique = !localStorage.getItem(trackingKey);

      const { data: current } = await supabase
        .from('standout_analytics')
        .select('*')
        .eq('standout_id', standoutId)
        .single();

      if (!current) {
        const insertData = {
          standout_id: standoutId,
          views: 0, unique_views: 0,
          shares: 0, unique_shares: 0,
          link_copies: 0, unique_link_copies: 0,
          downloads: 0, unique_downloads: 0
        };
        insertData[column] = 1;
        if (isUnique) insertData[uniqueColumn] = 1;
        await supabase.from('standout_analytics').insert([insertData]);
      } else {
        const updateData = { [column]: current[column] + 1 };
        if (isUnique) updateData[uniqueColumn] = current[uniqueColumn] + 1;
        await supabase.from('standout_analytics').update(updateData).eq('standout_id', standoutId);
      }

      if (isUnique) localStorage.setItem(trackingKey, 'true');
      return { success: true };
    } catch (err) {
      console.error('Standout analytics tracking error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const getAllStandoutAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('standout_analytics')
        .select(`
          *,
          standout:standouts (
            id,
            athlete_name,
            sport,
            week_label,
            status,
            org_name,
            team_name
          )
        `);
      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const getStandoutAnalyticsTotals = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('standout_analytics')
        .select('*');
      if (fetchError) throw fetchError;

      const totals = data.reduce((acc, item) => ({
        views: acc.views + (item.views || 0),
        unique_views: acc.unique_views + (item.unique_views || 0),
        shares: acc.shares + (item.shares || 0),
        unique_shares: acc.unique_shares + (item.unique_shares || 0),
        link_copies: acc.link_copies + (item.link_copies || 0),
        unique_link_copies: acc.unique_link_copies + (item.unique_link_copies || 0),
        downloads: acc.downloads + (item.downloads || 0),
        unique_downloads: acc.unique_downloads + (item.unique_downloads || 0),
      }), { views: 0, unique_views: 0, shares: 0, unique_shares: 0, link_copies: 0, unique_link_copies: 0, downloads: 0, unique_downloads: 0 });

      return { success: true, data: totals };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // BANNED WORDS FUNCTIONS
  // ========================================
  // NOTIFICATIONS
  // ========================================

  const getAdminNotifications = useCallback(async (limit = 40) => {
    if (!checkSupabase()) return { success: false, data: [] };
    try {
      const { data, error } = await supabase
        .from('admin_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      return { success: false, data: [] };
    }
  }, []);

  const markNotificationRead = useCallback(async (id) => {
    if (!checkSupabase()) return;
    try {
      await supabase
        .from('admin_notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', id);
    } catch (err) {
      console.error('markNotificationRead error:', err);
    }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    if (!checkSupabase()) return;
    try {
      await supabase
        .from('admin_notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('is_read', false);
    } catch (err) {
      console.error('markAllNotificationsRead error:', err);
    }
  }, []);

  // ========================================

  const getBannedWords = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('banned_words')
        .select('*')
        .order('word', { ascending: true });
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const addBannedWord = useCallback(async (word) => {
    try {
      const { data, error } = await supabase
        .from('banned_words')
        .insert([{ word: word.trim().toLowerCase() }])
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updateBannedWord = useCallback(async (id, word) => {
    try {
      const { data, error } = await supabase
        .from('banned_words')
        .update({ word: word.trim().toLowerCase() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const deleteBannedWord = useCallback(async (id) => {
    try {
      const { error } = await supabase
        .from('banned_words')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ========================================
  // EMAIL SUBSCRIBER FUNCTIONS
  // ========================================

  // Add email subscriber
  const addEmailSubscriber = useCallback(async (email) => {
    try {
      // Check if already exists
      const { data: existing } = await supabase
        .from('email_subscribers')
        .select('id')
        .eq('email', email)
        .single();

      if (existing) {
        return { success: true, message: 'Already subscribed' };
      }

      const { error: insertError } = await supabase
        .from('email_subscribers')
        .insert([{
          email,
          subscribed_at: new Date().toISOString()
        }]);

      if (insertError) throw insertError;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Get all email subscribers
  const getEmailSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('email_subscribers')
        .select('*')
        .order('subscribed_at', { ascending: false });

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ========================================
  // STANDOUTS FUNCTIONS
  // ========================================

  // ========================================
  // CURRENT WEEK FUNCTIONS
  // ========================================

  const getCurrentWeek = useCallback(async () => {
    if (!checkSupabase()) return { success: false, data: null };
    try {
      const { data, error } = await supabase
        .from('landing_page_settings')
        .select('current_week_label, current_week_date_range')
        .eq('id', 1)
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message, data: null };
    }
  }, []);

  const setCurrentWeek = useCallback(async (weekLabel, weekDateRange) => {
    if (!checkSupabase()) return { success: false };
    try {
      const { error } = await supabase
        .from('landing_page_settings')
        .update({
          current_week_label: weekLabel || '',
          current_week_date_range: weekDateRange || '',
          updated_at: new Date().toISOString(),
        })
        .eq('id', 1);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const _slugify = (str) => {
    if (!str) return '';
    return String(str)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // ── Organizations ──
  const getOrganizationBySlug = useCallback(async (slug) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: null };
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('slug', slug)
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message, data: null };
    }
  }, []);

  const getApprovedOrganizations = useCallback(async () => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: [] };
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('status', 'approved')
        .order('name', { ascending: true });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      return { success: false, error: err.message, data: [] };
    }
  }, []);

  const getOrganizations = useCallback(async () => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: [] };
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      return { success: false, error: err.message, data: [] };
    }
  }, []);

  const requestOrganization = useCallback(async (formData) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { data: orgData, error } = await supabase
        .from('organizations')
        .insert({
          name: formData.name,
          slug: _slugify(formData.name),
          sport: formData.sport || null,
          city: formData.city ? formData.city.trim() : null,
          state: formData.state ? formData.state.trim().toUpperCase() : null,
          contact_name: formData.contact_name || null,
          contact_email: formData.contact_email || null,
          phone: formData.phone || null,
          website: formData.website || null,
          status: 'pending',
        })
        .select('id')
        .single();
      if (error) throw error;
      _createAdminNotification(
        'organization',
        'New Organization Request',
        `${formData.name}${formData.city ? ` — ${formData.city}, ${formData.state || ''}` : ''}`,
        orgData?.id || null,
        '/dashboard/organizations'
      ).catch(() => {});
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const createOrganization = useCallback(async (formData) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase
        .from('organizations')
        .insert({
          name: formData.name,
          slug: _slugify(formData.name),
          sport: formData.sport || null,
          city: formData.city ? formData.city.trim() : null,
          state: formData.state ? formData.state.trim().toUpperCase() : null,
          contact_name: formData.contact_name || null,
          contact_email: formData.contact_email || null,
          phone: formData.phone || null,
          website: formData.website || null,
          status: 'approved',
        });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updateOrganizationStatus = useCallback(async (id, status) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const deleteOrganization = useCallback(async (id) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase.from('organizations').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const getOrgAthletes = useCallback(async (orgSlug, userId) => {
    if (!checkSupabase()) return { success: false, data: [] };
    try {
      let query = supabase
        .from('standouts')
        .select('athlete_name, team_name, sport, level, division')
        .eq('org_slug', orgSlug)
        .order('athlete_name', { ascending: true });
      if (userId) query = query.eq('submitted_by', userId);
      const { data, error } = await query;
      if (error) throw error;
      // Deduplicate by athlete_name
      const seen = new Set();
      const unique = (data || []).filter((s) => {
        if (!s.athlete_name || seen.has(s.athlete_name.toLowerCase())) return false;
        seen.add(s.athlete_name.toLowerCase());
        return true;
      });
      return { success: true, data: unique };
    } catch (err) {
      return { success: false, data: [], error: err.message };
    }
  }, []);

  // Public: submit a standout (status = pending)
  const submitStandout = useCallback(async (formData) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const payload = {
        status: 'pending',
        submitted_by: authUser?.id || null,
        submitter_name: formData.submitter_name || null,
        submitter_email: formData.submitter_email || null,
        org_name: formData.org_name,
        org_slug: _slugify(formData.org_name),
        org_short_name: formData.org_short_name || null,
        org_location: formData.org_location || null,
        athlete_name: formData.athlete_name,
        team_name: formData.team_name || null,
        sport: formData.sport,
        level: formData.level || null,
        division: formData.division || null,
        gender: formData.gender || null,
        award: formData.award || null,
        stats: formData.stats || [],
        photo_url: formData.photo_url || null,
        coach_name: formData.coach_name || null,
        coach_title: formData.coach_title || null,
        coach_note: formData.coach_note || null,
        game_details: formData.game_details || null,
        week_label: formData.week_label || null,
        week_date_range: formData.week_date_range || null,
        season: formData.season || null,
      };
      const { data, error: insertError } = await supabase
        .from('standouts')
        .insert([payload])
        .select()
        .single();
      if (insertError) throw insertError;
      if (data?.id) {
        _createAdminNotification(
          'standout',
          'New Standout Submission',
          `${formData.athlete_name} — ${formData.org_name}`,
          data.id,
          '/dashboard/standouts'
        ).catch(() => {});
      }
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: get all standouts (optionally filter by status)
  const getStandouts = useCallback(async (status = null) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: [] };
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('standouts').select('*').order('created_at', { ascending: false });
      if (status && status !== 'all') query = query.eq('status', status);
      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return { success: true, data: data || [] };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message, data: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  // Public: get visible standouts (published, or scheduled with past scheduled_at)
  // Note: 'approved' is intentionally NOT public — it's a queued/staging state.
  const getApprovedStandouts = useCallback(async (filters = {}) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: [] };
    setLoading(true);
    setError(null);
    try {
      const nowIso = new Date().toISOString();
      let query = supabase.from('standouts').select('*');

      const orParts = [
        `status.eq.published`,
        `and(status.eq.scheduled,scheduled_at.lte.${nowIso})`,
      ];
      query = query.or(orParts.join(','));

      if (filters.weekLabel) query = query.eq('week_label', filters.weekLabel);
      if (filters.orgSlug) query = query.eq('org_slug', filters.orgSlug);
      if (filters.sport) query = query.eq('sport', filters.sport);

      query = query.order('created_at', { ascending: false });

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return { success: true, data: data || [] };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message, data: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const getStandoutById = useCallback(async (id) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('standouts')
        .select('*')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: create standout directly (auto-published — immediately public-visible)
  const createStandoutDirect = useCallback(async (formData) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const payload = {
        status: formData.status || 'published',
        submitter_name: formData.submitter_name || null,
        submitter_email: formData.submitter_email || null,
        org_name: formData.org_name,
        org_slug: _slugify(formData.org_name),
        org_short_name: formData.org_short_name || null,
        org_location: formData.org_location || null,
        athlete_name: formData.athlete_name,
        athlete_user_id: formData.athlete_user_id || null,
        team_name: formData.team_name || null,
        sport: formData.sport,
        level: formData.level || null,
        division: formData.division || null,
        gender: formData.gender || null,
        award: formData.award || null,
        stats: formData.stats || [],
        photo_url: formData.photo_url || null,
        coach_name: formData.coach_name || null,
        coach_title: formData.coach_title || null,
        coach_note: formData.coach_note || null,
        game_details: formData.game_details || null,
        week_label: formData.week_label || null,
        week_date_range: formData.week_date_range || null,
        season: formData.season || null,
        scheduled_at: formData.scheduled_at || null,
        published_at: formData.status === 'published' ? new Date().toISOString() : null,
      };
      const { data, error: insertError } = await supabase
        .from('standouts')
        .insert([payload])
        .select()
        .single();
      if (insertError) throw insertError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: update standout (all fields)
  const updateStandoutCardImage = useCallback(async (id, cardImageUrl) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase
        .from('standouts')
        .update({ card_image_url: cardImageUrl, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const updateStandout = useCallback(async (id, formData) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const payload = { updated_at: new Date().toISOString() };
      const fields = [
        'status', 'submitter_name', 'submitter_email', 'org_name', 'org_short_name',
        'org_location', 'athlete_name', 'athlete_user_id', 'team_name', 'sport', 'level', 'division',
        'gender', 'award', 'stats', 'photo_url', 'coach_name', 'coach_title',
        'coach_note', 'game_details', 'week_label', 'week_date_range', 'season',
        'scheduled_at', 'published_at', 'featured',
      ];
      fields.forEach((f) => {
        if (formData[f] !== undefined) payload[f] = formData[f];
      });
      if (formData.org_name !== undefined) {
        payload.org_slug = _slugify(formData.org_name);
      }

      const { data, error: updateError } = await supabase
        .from('standouts')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (updateError) throw updateError;
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: search registered athletes to link a standout / spotlight to a
  // real profile (uses the SECURITY DEFINER RPC so it bypasses RLS).
  const searchAthletes = useCallback(async (searchTerm = '') => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured', data: [] };
    try {
      const { data, error } = await supabase.rpc('search_athletes_for_admin', {
        search_term: searchTerm || '',
      });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      return { success: false, error: err.message, data: [] };
    }
  }, []);

  // Admin: resolve linked athletes by id (for list columns + edit chips).
  const getAthletesByIds = useCallback(async (ids = []) => {
    const clean = (ids || []).filter(Boolean);
    if (!checkSupabase() || clean.length === 0) return { success: true, data: [] };
    try {
      const { data, error } = await supabase.rpc('get_athletes_for_admin', { p_ids: clean });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (err) {
      return { success: false, error: err.message, data: [] };
    }
  }, []);

  const toggleStandoutFeatured = useCallback(async (id, currentValue) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    try {
      const { data, error } = await supabase
        .from('standouts')
        .update({ featured: !currentValue, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Admin: change status (approve/reject/schedule/publish)
  const updateStandoutStatus = useCallback(async (id, status, scheduledAt = null) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const payload = { status, updated_at: new Date().toISOString() };
      if (status === 'scheduled' && scheduledAt) payload.scheduled_at = scheduledAt;
      if (status === 'published') payload.published_at = new Date().toISOString();
      const { data, error: updateError } = await supabase
        .from('standouts')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (updateError) throw updateError;

      // Admin notification on approve/reject
      const standoutName = data?.athlete_name || 'Submission';
      if (status === 'approved') {
        _createAdminNotification('standout', 'Standout Submission Approved',
          `${standoutName} was approved`, id, '/dashboard/standouts').catch(() => {});
      } else if (status === 'rejected') {
        _createAdminNotification('standout', 'Standout Submission Rejected',
          `${standoutName} was rejected`, id, '/dashboard/standouts').catch(() => {});
      }

      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: bulk status update
  const bulkUpdateStandoutStatus = useCallback(async (ids, status, scheduledAt = null) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const payload = { status, updated_at: new Date().toISOString() };
      if (status === 'scheduled' && scheduledAt) payload.scheduled_at = scheduledAt;
      if (status === 'published') payload.published_at = new Date().toISOString();
      const { data, error: updateError } = await supabase
        .from('standouts')
        .update(payload)
        .in('id', ids)
        .select();
      if (updateError) throw updateError;

      // Admin notification (summary) on bulk approve/reject
      const count = (data || []).length;
      if (count && (status === 'approved' || status === 'rejected')) {
        _createAdminNotification(
          'standout',
          `Standout Submissions ${status === 'approved' ? 'Approved' : 'Rejected'}`,
          `${count} submission${count === 1 ? '' : 's'} ${status}`,
          null,
          '/dashboard/standouts'
        ).catch(() => {});
      }

      return { success: true, data: data || [] };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: delete standout
  const deleteStandout = useCallback(async (id) => {
    if (!checkSupabase()) return { success: false, error: 'Supabase not configured' };
    setLoading(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('standouts')
        .delete()
        .eq('id', id);
      if (deleteError) throw deleteError;
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    loading,
    error,
    // Authentication
    signIn,
    signOut,
    updatePassword,
    // Nominations
    submitNomination,
    getNominations,
    updateNominationStatus,
    updateNominationVideoReviewed,
    deleteNomination,
    // Spotlights
    createSpotlight,
    createSpotlightDirect,
    getSpotlights,
    getSpotlightById,
    updateSpotlight,
    deleteSpotlight,
    // Featured Athletes
    getFeaturedAthletes,
    addToFeatured,
    updateFeaturedOrder,
    moveFeaturedUp,
    moveFeaturedDown,
    toggleFeaturedVisibility,
    removeFromFeatured,
    // Analytics
    trackAnalytics,
    getSpotlightAnalytics,
    getAllAnalytics,
    getAnalyticsTotals,
    // Standout Analytics
    trackStandoutAnalytics,
    getAllStandoutAnalytics,
    getStandoutAnalyticsTotals,
    // Notifications
    getAdminNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    // Banned Words
    getBannedWords,
    addBannedWord,
    updateBannedWord,
    deleteBannedWord,
    // Email
    addEmailSubscriber,
    getEmailSubscribers,
    // Upload
    uploadImage,
    // Admin Profile
    updateAdminProfileImage,
    fetchAdminProfileImage,
    // Current Week
    getCurrentWeek,
    setCurrentWeek,
    // Standouts
    submitStandout,
    getStandouts,
    getApprovedStandouts,
    getStandoutById,
    createStandoutDirect,
    updateStandout,
    toggleStandoutFeatured,
    updateStandoutStatus,
    bulkUpdateStandoutStatus,
    deleteStandout,
    updateStandoutCardImage,
    getOrgAthletes,
    // Athlete linking (Phase 1)
    searchAthletes,
    getAthletesByIds,
    // Organizations
    getOrganizationBySlug,
    getApprovedOrganizations,
    getOrganizations,
    requestOrganization,
    createOrganization,
    updateOrganizationStatus,
    deleteOrganization,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
